import {Injectable, Injector} from '@angular/core';
import {HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse} from '@angular/common/http';
import {Observable, ReplaySubject, of, throwError} from 'rxjs';
import {catchError, filter, switchMap, take, tap} from 'rxjs/operators';
import {AuthService} from '../services/auth.service';
import {AppService} from '../services/app.service';
import {LoggerService} from '../services/logger.service';
import {environment} from 'src/environments/environment';

// Credential exchanges: they carry their own proof, and a 401 from them means
// THAT credential was refused — it must never start a token-refresh cycle.
const NO_TOKEN_PATHS = new Set(['/auth/login', '/auth/social', '/auth/refresh-token']);

@Injectable({
  providedIn: 'root',
})
export class TokenInterceptor {
  private isRefreshing = false;
  private refreshedToken$ = new ReplaySubject<string>(1);
  private healed = false;

  constructor(
    private authService: AuthService,
    private injector: Injector
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let url: URL;
    try {
      url = new URL(req.url);
    } catch {
      // Relative or malformed URL — no host to route on, pass through untouched.
      return next.handle(req);
    }

    if (url.host == environment.HOST && !NO_TOKEN_PATHS.has(url.pathname)) {
      return this.authService.getTokenFromStorage().pipe(
        // The signup / build-quote flows reach API endpoints (e.g.
        // save-vehicle) BEFORE a new-API token has ever been minted — signup
        // only produces the legacy token, never `newtoken`. Sending the
        // request unauthenticated here 401s and dead-ends the quote. So when
        // no token is stored, mint one before proceeding instead of sending
        // bare.
        switchMap((token) => (token ? of(token) : this.resolveApiToken())),
        switchMap((token) => {
          if (!token) {
            return next.handle(req);
          }
          const newReq = req.clone({
            setHeaders: {Authorization: `Bearer ${token}`},
          });
          return next.handle(newReq).pipe(
            catchError((err) => {
              if (err instanceof HttpErrorResponse && err.status === 401) {
                return this.handleUnauthorized(req, next);
              }
              // RxJS 6: pass the error itself. The factory form
              // `throwError(() => err)` is RxJS 7 — on 6 callers received the
              // function, so no page could read err.status (a 404 "VIN not
              // matched" surfaced as a generic failure).
              return throwError(err);
            })
          );
        })
      );
    }

    return next.handle(req);
  }

  /**
   * Resolve a token for an API request when none is stored yet (signup /
   * build-quote flows reach API endpoints before any new-API login has run).
   * Mint a new-API token from the stored credentials via the same path the
   * on-401 refresh uses. If the login can't succeed (e.g. social signup, where
   * the stored password is a sentinel), emit null so the request goes out
   * unauthenticated — unchanged behaviour for genuinely anonymous requests.
   */
  private resolveApiToken(): Observable<string | null> {
    return this.authService.getTokenFromApi().pipe(
      tap(() => (this.healed = false)),
      catchError((err) => {
        this.healIfCredsRejected(err);
        return of(null);
      }),
      // Normalise falsy values (empty string / undefined) to null.
      switchMap((token) => of(token || null))
    );
  }

  /**
   * A refresh that fails with an explicit 401 means the server rejected the
   * STORED credentials themselves (password changed, or a poisoned/stale
   * reduced_user). Replaying them can never succeed, so every Node request
   * dies in a silent 401 loop while the long-lived legacy session keeps the
   * rest of the app looking healthy. Wipe the dead session and send the user
   * back to login — one re-login repairs the device permanently. Anything
   * other than a 401 (network, 5xx, missing creds) is NOT proof the creds are
   * bad and must never log the user out.
   */
  private healIfCredsRejected(err: unknown): void {
    // RxJS 6: throwError(() => err) delivers the FACTORY as the error value
    // (factory signatures arrived in RxJS 7). getTokenFromApi uses that form,
    // so unwrap once to reach the real error. Harmless no-op after an RxJS 7
    // upgrade makes the factory form behave as intended.
    if (typeof err === 'function') {
      try {
        err = (err as () => unknown)();
      } catch {
        return;
      }
    }
    if (!(err instanceof HttpErrorResponse) || err.status !== 401 || this.healed) {
      return;
    }
    this.healed = true;
    this.injector.get(LoggerService).logInformation('Session self-heal: stored credentials rejected, forcing re-login', {
      event: 'auth.selfheal.creds_rejected',
    });
    this.authService.forceLogout();
    this.injector.get(AppService).presentErrorToast('Your session has expired. Please log in again.');
  }

  private handleUnauthorized(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.refreshedToken$.pipe(
        filter((token) => !!token),
        take(1),
        switchMap((token) => {
          const newReq = req.clone({setHeaders: {Authorization: `Bearer ${token}`}});
          return next.handle(newReq);
        })
      );
    }

    this.isRefreshing = true;
    const cycle$ = (this.refreshedToken$ = new ReplaySubject<string>(1));

    // FIX A: Drive the refresh with its OWN standalone subscription so its
    // lifetime is independent of the initiating request. If the initiator
    // unsubscribes mid-refresh (page destroy / takeUntil / navigation — routine
    // in Ionic), the refresh HTTP call still completes and cycle$ still
    // emits/errors. Without this, the outer pipe's unsubscription would cancel
    // getTokenFromApi(), leaving isRefreshing=true forever and hanging all
    // future 401s for the app's lifetime.
    this.authService.getTokenFromApi().subscribe({
      next: (t) => {
        this.isRefreshing = false;
        this.healed = false;
        cycle$.next(t);
      },
      error: (e) => {
        this.isRefreshing = false;
        this.healIfCredsRejected(e);
        // Do NOT emit an empty token — waiting requests filter it out and the
        // refresh failure propagates instead of retrying with `Bearer `.
        cycle$.error(e);
      },
    });

    // Both the initiator and waiters go through the same cycle$ waiter pipe.
    return cycle$.pipe(
      filter((token) => !!token),
      take(1),
      switchMap((token) => {
        const newReq = req.clone({setHeaders: {Authorization: `Bearer ${token}`}});
        return next.handle(newReq);
      })
    );
  }
}
